import React, { useEffect, useState, useMemo } from 'react'
import HistoryIcon from '@mui/icons-material/History'
import moment from 'moment'
import styled from 'styled-components'
import { Moment, tz } from 'moment-timezone'

import Spinner from 'components/Spinner'
import { Panel } from 'components/Panel'

import Day from './Day'
import DateRangePicker from 'pages/shared/AlertsHistory/DateRangePicker'

import useServerlessAlertHistory from 'hooks/useServerlessAlertHistory'

import { shortDateWithDayOfWeek } from 'helpers/formatters'
import { updateAlertsSeverity } from 'helpers/alerts'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import { Alert } from 'types/Alert'

export type Props = {
  deviceId: string
  deviceTimeZone: string
  showDayRthCallback: (day: Moment) => void
}

export interface AlertsHistory {
  fromTime: number
  toTime: number
  moreHistory: boolean
  historyDetails: Array<Alert>
}

const AlertsHistory = React.memo(({ deviceId, deviceTimeZone, showDayRthCallback }: Props) => {
  const alertHistoryToTime = useMemo(() => tz(deviceTimeZone).endOf('day'), [deviceTimeZone])
  const defaultStart = alertHistoryToTime.clone().subtract(6, 'days').startOf('day')
  const defaultEnd = alertHistoryToTime.endOf('day')

  const [startDate, setStartDate] = useState(defaultStart.format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(defaultEnd.format('YYYY-MM-DD'))
  const [alerts, setAlerts] = useState<Array<Alert>>([])
  const [loadingState, setLoadingState] = useState({ isLoading: true, showSpinner: false })

  const { alertsHistory, isAlertsHistoryLoaded } = useServerlessAlertHistory(
    deviceId,
    startDate,
    endDate,
    deviceTimeZone
  )

  useEffect(() => {
    if (isAlertsHistoryLoaded) {
      setAlerts(updateAlertsSeverity(alertsHistory?.historyDetails) ?? [])
      setLoadingState({ isLoading: false, showSpinner: false })
    }
  }, [alertsHistory, isAlertsHistoryLoaded])

  const onDateRangeChange = (newStart: string, newEnd: string) => {
    setLoadingState({ isLoading: true, showSpinner: true })
    setStartDate(newStart)
    setEndDate(newEnd)
  }

  const groupedAlerts = alerts
    .reduce((acc, alert: Alert) => {
      const dateKey = tz(Number(alert.occurredAt) * 1000, alert.timeZone).startOf('day').valueOf()

      if (!acc[dateKey]) acc[dateKey] = []
      acc[dateKey].push(alert)
      return acc
    }, {} as Record<number, Array<Alert>>)

  return (
    <S.AlertHistory>
      <S.Header>
        <S.TitleSection>
          <HistoryIcon />
          <S.PanelTitle>Alert History</S.PanelTitle>
        </S.TitleSection>
        <S.DatePickerContainer>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChangeCallback={onDateRangeChange}
          />
        </S.DatePickerContainer>
      </S.Header>
      {loadingState.isLoading ? (
        <Spinner variant="page" />
      ) : (
        <S.AlertsHistoryContent>
          {alerts.length > 0 ? (
            <>
              <S.AlertsDateRange>
                Alerts occurring {startDate === endDate ? 'on' : 'between'} {shortDateWithDayOfWeek(moment.tz(startDate, deviceTimeZone))}{startDate === endDate ? '' : ` and ${shortDateWithDayOfWeek(moment.tz(endDate, deviceTimeZone))}`}
              </S.AlertsDateRange>
              <S.AlertDaysHistory>
                {Object.entries(groupedAlerts).sort((a, b) => Number(b[0]) - Number(a[0])).map(([key, value]) => (
                  <Day
                    key={key}
                    alerts={value}
                    timeZone={deviceTimeZone}
                    occurredDay={Number(key)}
                    showDayRthCallback={showDayRthCallback}
                  />
                ))}
              </S.AlertDaysHistory>
            </>
          ) : (
            <div>No alerts occurred {startDate === endDate ? 'on' : 'between'} {shortDateWithDayOfWeek(moment.tz(startDate, deviceTimeZone))}{startDate === endDate ? '' : ` and ${shortDateWithDayOfWeek(moment.tz(endDate, deviceTimeZone))}`}</div>
          )}
        </S.AlertsHistoryContent>
      )}
    </S.AlertHistory>
  )
})

export default AlertsHistory

const S = {
  AlertHistory: styled.div`
    margin: 0;
    min-height: 300px; // leave space for date picker calendar dropdown

    @media ${screenSize.medium} {
      width: 100%;
    }`,
  Header: styled(Panel.Header)`
    flex-direction: row;
    padding-right: 0;
    align-items: center;
    gap: 8px;

    @media ${screenSize.small} {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  `,
  TitleSection: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  PanelTitle: styled(Panel.Title)`
    font-size: 16px;
    text-decoration: underline;
  `,
  AlertsHistoryContent: styled.div`
    padding: 0px 25px;

    @media ${screenSize.small} {
      padding: 0;
    }
  `,
  AlertsDateRange: styled.div`
    padding-bottom: 10px;
  `,
  AlertDaysHistory: styled.div`
    & > :nth-child(n) {
      border-top: 1px solid ${color('divider')};
    }

    & > :last-child {
      border-bottom: 1px solid ${color('divider')};
    }
  `,
  DatePickerContainer: styled.div`
    margin-left: 15px;
    display: flex;
    align-items: center;

    @media ${screenSize.small} {
      margin-left: 0;
      margin-top: 8px;
      width: 100%;
    }
  `
}
